import React, { ReactNode, useCallback, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import i18next from 'i18next'
import { FormInput } from '@microservices/wiskey-react-components'
import { Delete } from '@mui/icons-material'
import { Box, Divider, IconButton, SxProps, Typography } from '@mui/material'

import { GANTT_BIND_TYPE, GENERATOR_INPUT_TYPE } from '@constants'
import { SelectOption } from '@types'

import { GANTT_BIND_TYPE_LIST } from '@gantt/components/GanttCreateOrEdit' //todo abstract list
import { getBindTypeOptionsByEnum } from '@gantt/helpers'
import { ExtOptionFilter } from '@gantt/types'

import { InputProps, ValueInputFactory } from './ValueInputFactory'

export type TextDict = Partial<Record<GANTT_BIND_TYPE_LIST, string>>

type Props = {
  bindType?: GANTT_BIND_TYPE_LIST
  isFieldsetMarkup?: boolean
  hasDivider?: boolean
  isShownBinding?: boolean
  containerName?: string
  fieldsetTitle?: string
  bindTypeTitle?: string
  blockLabel?: string
  bindTypeOptions?: SelectOption[]
  hintDict?: TextDict
  placeholderDict?: TextDict
  valueOptions?: ExtOptionFilter[]
  children?: ReactNode
  errorMessage?: string
  onDelete?: () => void
  containerSx?: SxProps
  isShowFormatDateField?: boolean
} & InputProps

export const BindTypeInputs = ({
  bindType,
  isFieldsetMarkup = false,
  hasDivider = false,
  isShownBinding = false,
  isDisabled = false,
  containerName,
  bindTypeOptions,
  hintDict,
  fieldsetTitle,
  bindTypeTitle,
  blockLabel,
  placeholderDict,
  valueOptions,
  children,
  errorMessage,
  onDelete,
  containerSx,
  isShowFormatDateField,
  ...inputProps
}: Props) => {
  const { watch, setValue } = useFormContext()
  const path = (key: string) => (containerName ? `${containerName}.${key}` : `${key}`)

  const watchBinding: GANTT_BIND_TYPE_LIST = bindType || watch(path('bindType'))
  const selectOptions = bindTypeOptions || getBindTypeOptionsByEnum(GANTT_BIND_TYPE)
  const hint = useMemo(() => hintDict && hintDict[watchBinding], [hintDict, watchBinding])

  const placeholder = useMemo(
    () => placeholderDict && placeholderDict[watchBinding],
    [placeholderDict, watchBinding]
  )

  const handleClearOnChangeBinding = useCallback((bindType: string | number) => {
    setValue(path(''), { bindType })
  }, [])

  return (
    <Box
      component={isFieldsetMarkup ? 'fieldset' : 'div'}
      mb={2}
      position='relative'
      pt={onDelete ? 4 : 0}
      sx={containerSx}
    >
      {fieldsetTitle && <legend>{fieldsetTitle}</legend>}
      {Boolean(onDelete) && (
        <Box
          display='flex'
          justifyContent='end'
          pb={1}
          position='absolute'
          sx={{
            position: 'absolute',
            right: 8,
            top: -6,
          }}
        >
          <IconButton size='small' onClick={onDelete}>
            <Delete fontSize='small' />
          </IconButton>
        </Box>
      )}

      {blockLabel && (
        <Typography pb={1} pt={1} variant={'body1'}>
          {blockLabel}
        </Typography>
      )}
      {(Boolean(selectOptions && selectOptions?.length > 1) || isShownBinding) && (
        <FormInput
          inputType={GENERATOR_INPUT_TYPE.SELECT}
          label={bindTypeTitle ? bindTypeTitle : i18next.t('label.bindType')}
          name={path('bindType')}
          selectNoItemsMessage={i18next.t('ganttCreate.common.noItems')}
          selectOptions={selectOptions}
          onChangeSelect={handleClearOnChangeBinding}
        />
      )}
      <ValueInputFactory
        errorMessage={errorMessage}
        getPath={path}
        hint={hint}
        isDisabled={isDisabled}
        isShowFormatDateField={isShowFormatDateField}
        options={valueOptions}
        placeholder={placeholder}
        type={watchBinding}
        {...inputProps}
      >
        {children}
      </ValueInputFactory>
      {hasDivider && <Divider sx={{ borderWidth: 1, mb: 1, mt: 1 }} />}
    </Box>
  )
}
